import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import StaticPagesApi from 'libs/api/website/staticPages';
import AuthService from 'services/Auth';

const useStaticPages = () => {
  const [staticPages, setStaticPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [shoppingCenters, setShoppingCenters] = useState([]);
  const [loadingShoppingCenters, setLoadingShoppingCenters] = useState(false);
  
  const navigate = useNavigate();
  const paramsRef = useRef({});

  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchStaticPages = useCallback(async (params = {}) => {
    if (!checkAuth()) return;

    setLoading(true);
    try {
      const response = await StaticPagesApi.list(params);
      
      if (response.data.success) {
        setStaticPages(response.data.static_pages || []);
        setTotal(response.data.pagination?.total_count || 0);
      } else {
        setStaticPages([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching static pages:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load static pages');
      }
    } finally {
      setLoading(false);
    }
  }, [checkAuth, navigate]);

  const fetchStaticPage = useCallback(async (id) => {
    if (!checkAuth()) return null;

    try {
      const response = await StaticPagesApi.show(id);
      if (response.data.success) {
        return response.data.static_page;
      }
      return null;
    } catch (error) {
      console.error('Error fetching static page:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load static page');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const updateStaticPage = useCallback(async (id, data) => {
    if (!checkAuth()) return null;

    try {
      const response = await StaticPagesApi.update(id, data);
      if (response.data.success) {
        return response.data.static_page;
      }
      return null;
    } catch (error) {
      console.error('Error updating static page:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to update static page');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const uploadFile = useCallback(async (file) => {
    if (!checkAuth()) return null;

    try {
      const response = await StaticPagesApi.upload(file);
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to upload file');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const fetchFilters = useCallback(async () => {
    if (!checkAuth()) return;
    setLoadingShoppingCenters(true);
    try {
      const response = await StaticPagesApi.getFilters(); 
      const shoppingCentersRaw = response.data.shopping_centers || [];
      setShoppingCenters(shoppingCentersRaw);
    } catch (error) {
      console.error('Error fetching filters:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error('Failed to load filters');
      }
    } finally {
      setLoadingShoppingCenters(false);
    }
  }, [checkAuth, navigate]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    const params = {
      page: currentPage,
      per: pageSize,
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    });
    paramsRef.current = params;
    fetchStaticPages(params);
  }, [currentPage, pageSize, filters, fetchStaticPages]);

  const updateFilters = useCallback((newFilters) => {
    const filteredFilters = {};
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        filteredFilters[key] = value;
      }
    });
    
    if (Object.keys(filteredFilters).length === 0) {
      setFilters({});
    } else {
      setFilters(prev => ({ ...prev, ...filteredFilters }));
    }
    setCurrentPage(1); 
  }, []);

  const handlePageChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  return {
    staticPages,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingShoppingCenters,
    updateFilters,
    handlePageChange,
    updateStaticPage,
    fetchStaticPage,
    uploadFile,
    refetch: () => fetchStaticPages(paramsRef.current),
  };
};

export default useStaticPages; 